import {DateTime} from "luxon";

export interface AsyncValidatorFunctionContext<T> {
    returnResult: (v: T) => unknown;
}

export type SyncValidatorFunc<T = boolean> = (params: unknown[], originalParams?: unknown[]) => T;

export type AsyncSyncValidatorFunc<T = boolean> = (
    this: AsyncValidatorFunctionContext<T>,
    params: unknown[],
    originalParams?: unknown[],
) => Promise<void>;

export type ValidatorFunc<T = boolean> = SyncValidatorFunc<T> | AsyncSyncValidatorFunc<T>;
export interface ValidatorEntry {
    func: ValidatorFunc; //TODO: differentiate between sync and async?
    isAsync?: boolean;
}

export type CustomValidatorSet = Record<string, ValidatorEntry>;

const nameRegex = /^([\p{L}]{1,26})(( |.|\. |-|')([\p{L}]{1,26})){0,3}$/giu;

const yearMonthToDateTime = (value: string) => DateTime.fromISO(`${value}-01`, {zone: "utc"});

const getElevenBasedChecksum = (value: string, weights: number[]) => {
    const padded = value.padStart(weights.length, "0");
    const sum = [...padded].reduce((acc, digit, i) => acc + Number(digit) * weights[i], 0);
    return sum % 11;
};

const validators: CustomValidatorSet = {
    name_unicode: {
        func: ([value]) => {
            return String(value || "").match(nameRegex) !== null;
        },
        isAsync: false,
    },

    cz_birth_num: {
        func: ([value]) => {
            const v = String(value);
            const cleaned = v.replace(/\D/g, "");
            if (!/^\d{9,10}$/.test(cleaned)) {
                return false;
            }
            const year = parseInt(cleaned.substring(0, 2), 10);
            const month = parseInt(cleaned.substring(2, 4), 10);
            const day = parseInt(cleaned.substring(4, 6), 10);
            const fullYear = year + (year < 54 ? 2000 : 1900);
            let adjustedMonth = month;
            if (month > 50) {
                adjustedMonth -= 50;
            } else if (month > 20 && fullYear >= 2004) {
                adjustedMonth -= 20;
            }
            const date = new Date(fullYear, adjustedMonth - 1, day);
            if (
                date.getFullYear() !== fullYear ||
                date.getMonth() + 1 !== adjustedMonth ||
                date.getDate() !== day
            ) {
                return false;
            }
            if (cleaned.length === 10) {
                const mod = parseInt(cleaned, 10) % 11;
                if (mod !== 0) {
                    return false;
                }
            }
            return true;
        },
        isAsync: false,
    },

    cz_bank_prefix: {
        func: ([value]) => {
            const v = String(value);
            if (!v || v.length > 6 || !/^\d+$/.test(v)) {
                return false;
            }
            return getElevenBasedChecksum(v, [10, 5, 8, 4, 2, 1]) === 0;
        },
        isAsync: false,
    },

    cz_bank_account: {
        func: ([value]) => {
            const v = String(value);
            if (!v || v.length > 10 || !/^\d+$/.test(v)) {
                return false;
            }
            return getElevenBasedChecksum(v, [6, 3, 7, 9, 10, 5, 8, 4, 2, 1]) === 0;
        },
        isAsync: false,
    },

    digits_only: {
        func: ([value]) => {
            return String(value || "").match(/^\d+$/) !== null;
        },
        isAsync: false,
    },

    year_month_interval: {
        func: (args) => {
            const [value, start, end] = args.map((arg) => {
                if (typeof arg === "string") {
                    return arg.length === 7
                        ? yearMonthToDateTime(arg as string)
                        : DateTime.fromISO(arg);
                } else if (arg instanceof Date) {
                    return DateTime.fromJSDate(arg);
                }
                return DateTime.invalid("Invalid type");
            });
            if (!value.isValid) {
                return false;
            }
            if (args[1]) {
                if (!start.isValid || value < start) {
                    return false;
                }
            }
            if (args[2]) {
                if (!end.isValid || value > end) {
                    return false;
                }
            }
            return true;
        },
        isAsync: false,
    },
};

export {validators};
export default validators;
